import { makeObservable, observable, action, computed, runInAction } from 'mobx';
import { ColumnTypes } from '../models/SheetModel';

export class SidePanelEditorViewModel {
    sheetDataStore = null;
    selectedRowId = null;
    
    constructor(sheetDataStore) {
        this.sheetDataStore = sheetDataStore;
        
        makeObservable(this, {
            selectedRowId: observable,
            setSelectedRow: action,
            updateField: action,
            resetChanges: action,
            cleanup: action,
            columns: computed,
            isLoading: computed,
            error: computed,
            availablePersonOptions: computed,
            updateColumnHeading: action,
            updateColumnType: action,
            updateColumnValidation: action,
            updateValidationOptionsForColumn: action,
            addColumnLeft: action,
            addColumnRight: action,
            moveColumnLeft: action,
            moveColumnRight: action,
            deleteColumn: action,
            getColumnOptionStyles: action,
            setColumnOptionStyle: action,
            removeColumnOptionStyle: action,
            styleEditorColumnId: observable,
            openColumnStyleEditor: action,
            closeColumnStyleEditor: action,
        });
    }
    
    get isLoading() {
        return this.sheetDataStore?.isLoading ?? false;
    }
    
    get error() {
        return this.sheetDataStore?.error ?? null;
    }
    
    get columns() {
        const cols = this.sheetDataStore?.currentSheet?.columns ?? [];
        // Return the raw column objects from the store
        return cols;
    }
    
    get availablePersonOptions() {
        // console.log('Getting availablePersonOptions, sheetDataStore:', this.sheetDataStore);
        
        if (!this.sheetDataStore) {
            // console.warn('No sheetDataStore available for person options');
            return [];
        }
        
        const personColumns = this.columns.filter(col => col.type?.id === 'PERSON');
        if (personColumns.length > 0) {
            const validationOptions = personColumns[0].validationOptions;
            if (validationOptions && validationOptions.length > 0) {
                // console.log('Using validation options from PERSON column:', validationOptions);
                return validationOptions.map(option => ({
                    value: option,
                    label: option
                }));
            }
        }
        
        if (this.sheetDataStore.users && Array.isArray(this.sheetDataStore.users)) {
            // console.log('Found users array in sheetDataStore:', this.sheetDataStore.users);
            return this.sheetDataStore.users.map(user => ({
                value: user.id || user.name,
                label: user.name || user.id
            }));
        }
        
        if (this.sheetDataStore.currentSheet?.validationData?.persons) {
            // console.log('Found persons in validationData:', this.sheetDataStore.currentSheet.validationData.persons);
            return this.sheetDataStore.currentSheet.validationData.persons.map(person => ({
                value: typeof person === 'string' ? person : (person.id || person.name),
                label: typeof person === 'string' ? person : (person.name || person.id)
            }));
        }
        
        const personValues = new Set();
        if (this.sheetDataStore.currentSheet?.rows) {
            for (const [_, rowData] of this.sheetDataStore.currentSheet.rows) {
                for (const column of personColumns) {
                    const cellValue = rowData[column.id]?.value;
                    if (cellValue) {
                        personValues.add(cellValue);
                    }
                }
            }
        }
        
        if (personValues.size > 0) {
            // console.log('Extracted person values from rows:', Array.from(personValues));
            return Array.from(personValues).map(value => ({
                value: value,
                label: value
            }));
        }
        
        // console.warn('No person options found in any expected location');
        return [];
    }
    
    setSelectedRow(rowId) {
        runInAction(() => {
            this.selectedRowId = rowId;
            // console.log('SidePanelEditorViewModel: Selected row set to', rowId);
        });
    }
    
    getFieldValue(columnId) {
        if (!this.selectedRowId || !this.sheetDataStore?.currentSheet) {
            return null;
        }
        
        const row = this.sheetDataStore.currentSheet.rows.get(this.selectedRowId);
        if (!row) return null;
        
        // Find the column by ID to get its heading
        const column = this.columns.find(col => col.id === columnId);
        if (!column) return null;
        
        // Access row data using column.id
        return row[column.id]?.value ?? null;
    }
    
    async updateField(columnId, value) {
        if (!this.selectedRowId || !this.sheetDataStore?.currentSheet) {
            console.warn('Cannot update field: No row selected or sheet available');
            return;
        }
        
        console.log(`SidePanelEditorViewModel: Updating field for item ${this.selectedRowId}, column ${columnId} with value:`, value);
        
        // Find the column by ID to get its heading
        const column = this.columns.find(col => col.id === columnId);
        if (!column) {
            console.error(`Column with ID ${columnId} not found`);
            return;
        }
        
        try {
            // Call the sheetDataStore's updateCell method directly with the row ID, column ID, and value
            await this.sheetDataStore.updateCell(this.selectedRowId, columnId, value);
            console.log(`SidePanelEditorViewModel: Cell updated successfully: row=${this.selectedRowId}, column=${columnId}, value=`, value);
            
            // Force a refresh of the UI by notifying observers
            runInAction(() => {
                // This empty action forces MobX to notify observers that something changed
                // It's a workaround for when the actual change happens in a different store
                this.selectedRowId = this.selectedRowId;
            });
        } catch (error) {
            console.error('SidePanelEditorViewModel: Error updating cell:', error);
        }
    }
    
    async updateValidationOptionsForColumn(columnId, optionValue, actionType) {
        if (!this.sheetDataStore || !columnId || !optionValue) return;

        const column = this.columns.find(col => col.id === columnId);
        if (!column) {
            console.error(`SidePanelEditorViewModel: Column ${columnId} not found for validation update.`);
            return;
        }

        // Ensure validationOptions exists and is an array
        const currentOptions = Array.isArray(column.validationOptions) ? [...column.validationOptions] : [];
        const optionExists = currentOptions.includes(optionValue);

        let updatedOptions = currentOptions;

        if (actionType === 'create' && !optionExists) {
            updatedOptions = [...currentOptions, optionValue];
            console.log(`SidePanelEditorViewModel: Adding option "${optionValue}" to validation for column ${columnId}`);
        } else if (actionType === 'remove' && optionExists) {
            updatedOptions = currentOptions.filter(opt => opt !== optionValue);
            console.log(`SidePanelEditorViewModel: Removing option "${optionValue}" from validation for column ${columnId}`);
        } else {
            // No change needed (e.g., trying to add existing or remove non-existent)
            return;
        }

        try {
            // Use the existing method to update the store
            await this.updateColumnValidation(columnId, updatedOptions);
            console.log(`SidePanelEditorViewModel: Column ${columnId} validation options updated.`);
        } catch (error) {
            console.error(`SidePanelEditorViewModel: Error updating validation options for column ${columnId}:`, error);
            // Optionally revert local state or show error
        }
    }
    
    resetChanges() {
        // console.log('SidePanelEditorViewModel: Reset called (may have limited effect)');
    }
    
    async updateColumnHeading(columnId, newHeading) {
        if (!this.sheetDataStore) return;
        try {
            await this.sheetDataStore.updateColumnHeading(columnId, newHeading);
            // console.log('SidePanelEditorViewModel: Column heading updated');
        } catch (error) {
            // console.error('SidePanelEditorViewModel: Error updating column heading:', error);
        }
    }
    
    async updateColumnType(columnId, newType) {
        if (!this.sheetDataStore) return;
        try {
            await this.sheetDataStore.updateColumnType(columnId, newType);
            // console.log('SidePanelEditorViewModel: Column type updated');
        } catch (error) {
            // console.error('SidePanelEditorViewModel: Error updating column type:', error);
        }
    }
    
    async updateColumnValidation(columnId, options) {
        if (!this.sheetDataStore) return;
        try {
            await this.sheetDataStore.updateColumnValidationOptions(columnId, options);
            // console.log('SidePanelEditorViewModel: Column validation updated');
        } catch (error) {
            // console.error('SidePanelEditorViewModel: Error updating column validation:', error);
        }
    }
    
    async addColumnLeft(relativeColumnId) {
        if (!this.sheetDataStore) return;
        try {
            const newHeading = `New Column`;
            await this.sheetDataStore.addColumn(
                newHeading,
                ColumnTypes.SINGLE_LINE_TEXT,
                { relativeTo: relativeColumnId, position: 'left' }
            );
            // console.log('SidePanelEditorViewModel: Column added left');
        } catch (error) {
            // console.error('SidePanelEditorViewModel: Error adding column left:', error);
        }
    }
    
    async addColumnRight(relativeColumnId) {
        if (!this.sheetDataStore) return;
        try {
            const newHeading = `New Column`;
            await this.sheetDataStore.addColumn(
                newHeading,
                ColumnTypes.SINGLE_LINE_TEXT,
                { relativeTo: relativeColumnId, position: 'right' }
            );
            // console.log('SidePanelEditorViewModel: Column added right');
        } catch (error) {
            // console.error('SidePanelEditorViewModel: Error adding column right:', error);
        }
    }
    
    async moveColumnLeft(columnId) {
        if (!this.sheetDataStore) return;
        try {
            const currentIndex = this.columns.findIndex(col => col.id === columnId);
            if (currentIndex > 0) {
                await this.sheetDataStore.moveColumnToPosition(columnId, currentIndex - 1);
                // console.log('SidePanelEditorViewModel: Column moved left');
            }
        } catch (error) {
            // console.error('SidePanelEditorViewModel: Error moving column left:', error);
        }
    }
    
    async moveColumnRight(columnId) {
        if (!this.sheetDataStore) return;
        try {
            const currentIndex = this.columns.findIndex(col => col.id === columnId);
            const storeColumns = this.sheetDataStore?.currentSheet?.columns ?? [];
            if (currentIndex < storeColumns.length - 1) {
                await this.sheetDataStore.moveColumnToPosition(columnId, currentIndex + 1);
                // console.log('SidePanelEditorViewModel: Column moved right');
            }
        } catch (error) {
            // console.error('SidePanelEditorViewModel: Error moving column right:', error);
        }
    }
    
    async deleteColumn(columnId) {
        if (!this.sheetDataStore) return;
        try {
            await this.sheetDataStore.deleteColumn(columnId);
            // console.log('SidePanelEditorViewModel: Column deleted');
        } catch (error) {
            // console.error('SidePanelEditorViewModel: Error deleting column:', error);
        }
    }
    
    cleanup() {
        // Perform any cleanup needed when the component unmounts
        this.selectedRowId = null;
    }
    
    clearAllFieldValues() {
        // This method is called when switching between items to ensure no stale values persist
        console.log('SidePanelEditorViewModel: Clearing all field values');
        
        // We don't actually need to clear values in the store since we're just changing the selected row
        // The values are stored per row in the sheet data store
        // This is just a hook that ensures we're properly handling item changes
        
        // Force a refresh of the UI by notifying observers that something changed
        runInAction(() => {
            // This empty action forces MobX to notify observers
            this.selectedRowId = this.selectedRowId;
        });
    }

    getColumnOptionStyles(columnId) {
        if (!this.sheetDataStore?.currentSheet) return undefined;
        const column = this.columns.find(col => col.id === columnId);
        return column?.optionStyles; // Directly return the observable map from the column model
    }

    async setColumnOptionStyle(columnId, optionValue, styleObject) {
        if (!this.sheetDataStore) return;
        console.log(`[SidePanelEditorViewModel] Setting style for column ${columnId}, option "${optionValue}":`, styleObject);
        try {
            await this.sheetDataStore.setColumnOptionStyle(columnId, optionValue, styleObject);
        } catch (error) {
            console.error(`Error setting option style for column ${columnId}, option ${optionValue}:`, error);
            throw error;
        }
    }

    async removeColumnOptionStyle(columnId, optionValue) {
        if (!this.sheetDataStore) return;
        console.log(`[SidePanelEditorViewModel] Removing style for column ${columnId}, option "${optionValue}"`);
        try {
            await this.sheetDataStore.removeColumnOptionStyle(columnId, optionValue);
        } catch (error) {
            console.error(`Error removing option style for column ${columnId}, option ${optionValue}:`, error);
            throw error;
        }
    }

    // Add observable state and actions for style editor
    styleEditorColumnId = null;

    openColumnStyleEditor(columnId) {
        console.log(`Opening style editor for column: ${columnId}`);
        this.styleEditorColumnId = columnId;
    }

    closeColumnStyleEditor() {
        this.styleEditorColumnId = null;
    }
} 